<template>
  <div
    v-if="shouldShowBanner"
    class="notification-banner fix-class"
  >
    <b-alert
      v-for="(banner, index) in bannerData"
      :key="index"
      class="alert-container d-flex align-items-center"
      show
      dismissible
      :variant="null"
      @dismissed="bannerClosed(index)"
    >
      <div class="alert-row d-flex align-items-center w-100">
        <feather-icon
          icon="AlertCircleIcon"
          size="22"
          class="alert-icon flex-shrink-0 mr-50"
        />
        <div class="alert-body">
          <p class="notification-subject mb-0">
            <strong>
              {{ banner.subject }}
            </strong>
          </p>
          <div
            class="notification-content"
            :title="getPlainContent(banner.notification_body)"
          >
            <span class="notification-content-text">
              {{ getContentPreview(banner.notification_body) }}
            </span>
            <a
              v-if="hasClickHereLink(banner.notification_body)"
              class="notification-content-link"
              href="javascript:void(0)"
              @click="handleClick()"
            >click here</a>
          </div>
        </div>
        <b-button
          v-if="banner.notification_body && getPlainContent(banner.notification_body).length > 100"
          variant="danger"
          class="btn-see-more"
          @click="handleCickForNotice"
        >
          SEE MORE
        </b-button>
      </div>
    </b-alert>
  </div>
</template>

<script>
import { BAlert } from 'bootstrap-vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessage from '@core/mixins/ErrorMessage'

const bannerRepository = RepositoryFactory.get('banner')
const MerchantSelfRepository = RepositoryFactory.get('SelfRegister')

export default {
  components: {
    BAlert,
  },
  mixins: [ErrorMessage],
  data() {
    return {
      bannerData: null,
      isBannnerShown: false,
      isMerchant: -1,
      color: '',
      bannerHeight: '5.5em',
      activeTermsId: null,
      termsContent: '',
    }
  },
  computed: {
    shouldShowBanner() {
      return this.isBannnerShown && (this.$route.name !== 'notices' && this.$route.name !== 'login')
    },
  },
  mounted() {
    this.fetchBannerData()
    this.getActiveTermsAndCondition()
  },
  methods: {
    async getActiveTermsAndCondition() {
      try {
        const response = await MerchantSelfRepository.getActiveTermsAndCondition()
        const { data } = response.data
        if (data) {
          this.activeTermsId = data.id
          this.termsContent = data.content
        } else {
          this.activeTermsId = null
          this.termsContent = null
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    handleClick() {
      if (this.termsContent.startsWith('https') && this.termsContent.endsWith('.pdf')) {
        window.open(this.termsContent, '_blank')
      } else if (this.termsContent) {
        const newWindow = window.open('', '_blank')
        newWindow.document.open()
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Terms & Conditions</title>
            <link
              href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/4.6.0/css/bootstrap.min.css"
              rel="stylesheet"
            />
          </head>
          <body>
            <div class="container mt-5">
              <div class="card">
                <div class="card-body px-5">
                  ${this.termsContent}
                  <div class="card mt-2 border-0 shadow-none print-button-section">
                    <div class="card-body d-flex justify-content-center">
                      <button class="print-button btn btn-outline-success" style="display: inline-block;" onclick="window.print()">Print</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <style>
              @media print {
                .print-button-section {
                  display: none; !important;
                }
                .card-body {
                  box-sizing: border-box;
                  padding: 20px !important;
                }
                .card {
                  box-sizing: border-box;
                  border: 1px solid #c7c7cd !important;
                  margin: 0 !important;
                  max-width: 100%;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
                .container {
                  max-width: 100%;
                  padding: 0 15px; /* Ensure consistent padding */
                }
              }
            </style>
          </body>
          </html>
        `)
        newWindow.document.close()
      }
    },
    hasClickHereLink(content) {
      return content && content.includes('click here')
    },
    getPlainContent(content) {
      if (!content) return ''
      return this.removeHtmlTags(content)
    },
    getContentPreview(content) {
      if (!content) return ''
      const plain = this.getPlainContent(content)
      if (!this.hasClickHereLink(content)) {
        return plain
      }
      return plain.split('click here')[0].trimEnd()
    },
    closeBanner() {
      // Give a slight delay before hiding the banner
      setTimeout(() => {
        this.isBannerShown = false
      }, 300)
    },
    removeHtmlTags(input) {
      const doc = new DOMParser().parseFromString(input, 'text/html')
      return doc.body.textContent || ''
    },
    async fetchBannerData() {
      try {
        const bannerDataResponse = this.$store.getters.isLoggedIn
          ? await bannerRepository.getActiveBannerAuth()
          : await bannerRepository.getActiveBannerNoAuth()
        this.bannerData = bannerDataResponse.data.data
        if (this.bannerData && this.bannerData.length > 0) {
          this.isBannnerShown = true
        } else {
          this.isBannnerShown = false
        }
      } catch (fetchError) {
        // Check if error is due to unauthenticated access
        if (fetchError.response && fetchError.response.status === 401 && this.$route.name === 'login') {
          // User is unauthenticated, fetch unauthenticated banner data
          try {
            const unauthBannerDataResponse = await bannerRepository.getActiveBannerNoAuth()
            this.bannerData = unauthBannerDataResponse.data.data
            if (this.bannerData && this.bannerData.length > 0) {
              this.isBannnerShown = true
            } else {
              this.isBannnerShown = false
            }
          } catch (error) {
            // Handle error fetching unauthenticated banner data
            this.convertAndNotifyError(error)
          }
        } else {
          // Handle other errors
          this.convertAndNotifyError(fetchError)
        }
      }
    },
    // To check if the banner is expired or not
    isExpired(date) {
      const now = new Date()
      return date < now
    },
    bannerClosed(index) {
      this.bannerData.splice(index, 1)
      if (this.bannerData.length === 0) {
        this.isBannnerShown = false
      }
    },
    handleCickForNotice() {
      this.$router.push('/notices')
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/themes/bordered-layout.scss";

@media (max-width: 600px) {
  .alert-body {
    font-size: 8px;
    height: auto !important;
  }
  .notification-content{
    max-width: 100%;
  }
  .notification-content p{
    line-height: 0.55rem !important;
  }
  .notification-subject{
    word-wrap: break-word;
    white-space: normal;
    max-width: 100%;
    line-height: 0.55rem !important;
  }
}

.alert-container {
  background-color: #D936331A;
  padding: 0.5rem 3rem 0.5rem 1rem;
  overflow: hidden;
  max-width: 100%;
  border: 1px solid #B51A1E;
}

.alert-row {
  min-width: 0;
}

.notification-banner {
  text-align: left;
  width: 100%;
  max-width: 100%;
  transition: height 0.3s ease;
  border-radius: 0px;
  align-items: center;
  justify-content: start;
  margin-bottom: 0;
  padding-bottom: 0;
  color: white;
  overflow: hidden;
  box-sizing: border-box;
}

.notification-subject {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  margin-bottom: 5px !important;
  line-height: 1.25rem !important;
  font-size: 1.2rem;
  color: #B51A1E;
}

.alert-body {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.notification-content {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  line-height: 1.25rem !important;
  font-size: 1rem;
  color: #434657;
}

.notification-content-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1 1 auto;
}

.notification-content-link {
  flex-shrink: 0;
  margin-left: 0.25rem;
  white-space: nowrap;
}

:root {
  --dark-bg: #1e1e1e;
  --dark-text: #ffffff;
}
.alert.alert-dismissible .close {
  padding: 1.75rem 1rem;
  color: #B51A1E;
  z-index: 1;
}
.fix-class {
  position: relative;
}

.alert-icon {
  color: #B51A1E;
}

.btn-see-more {
  background-color: #AA2F32;
  border-color: #AA2F32;
  color: white;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  white-space: nowrap;
  flex-shrink: 0;
  border-radius: 15px;
  cursor: pointer;
}

@media (max-width: 1024px) and (min-width: 769px) {
  .alert-body {
    font-size: 10px;
  }
  .notification-content{
    max-width: 100%;
  }
  .notification-content p{
    line-height: 0.75rem !important;
  }
  .notification-subject{
    word-wrap: break-word;
    white-space: normal;
    max-width: 90%;
    line-height: 1.25rem !important;
  }
}
</style>
